/* eslint-disable no-nested-ternary */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { RootState } from '../store/create-store';
import Layout from '../common/components/layout/layout.component';
import NewHome from './new/container/login.container';
import Loader from '../common/components/loader/loader.component';
import CustomSnackbar from '../common/components/custom-snackbar/custom-snackbar.component';
import CookiePopUp from '../common/components/accept-cookies/cookie-popup.component';
import QuickTourHome from './quick-tour/components/home.component';
import { useUserData } from '../common/contexts/user-data.context';
import { isDevAuthBypass } from '../common/dev/dev-auth-bypass';
import { onboardingRouteIsOpen } from '../common/routing/onboarding-routing';
import {
  isPurchaseDeepLink,
  signInForPurchase,
} from '../common/routing/purchase-sign-in';
import { stashOnboardingLoginAlert } from '../common/lib/onboarding-login-alert';
import InvestorContainer from './investors/investors.container';
import patentsFoot from './patents/container/patentsFoot';
import PublicProfilePage from './public-profile/public-profile.page';
import RelationshipListSignIn from './public-profile/relationship-list-sign-in';
import QuickLinkGoPage from './public-profile/quick-link-go.page';
import { premiumProfileChannel, profilePeoplePath, quickLinkGo } from './premium-profile/profile-path';

const Home = lazy(() => import('./home/home.index'));
const Leaderboard = lazy(
  () => import('./home/routes/dashboard/container/leaderboard'),
);
const CookiePolicy = lazy(
  () => import('./cookie-policy/container/cookie-policy.component'),
);
const TrustPolicy = lazy(
  () => import('./trust-policy/container/trust-policy.component'),
);
const TermsAndConditions = lazy(
  () =>
    import('./terms-and-conditions/container/terms-and-conditions.container'),
);
const PotentialCalculator2 = lazy(
  () => import('./potential-calculator-2/potential-calculator-2.component'),
);
const QuickTour = lazy(() => import('./quick-tour/quick-tour.component'));
const UnsubscribePromotionalEmails = lazy(
  () =>
    import(
      './unsubscribe-promotional-emails/unsubscribe-promotional-email.component'
    ),
);
const QuickStart = lazy(() => import('./quick-start/quickstart'));
const PrivacyPolicy = lazy(
  () => import('./privacy-policy/container/privacy-policy.container'),
);
const Whitepaper = lazy(
  () => import('./whitepaper/container/whitepaper.container'),
);
const AboutUs = lazy(() => import('./about-us/container/about-us.container'));
const VerifyEmail = lazy(
  () => import('./home/routes/verify/verify-email.component'),
);
const ShareWizard = lazy(() => import('./share-wizard/share-wizard.component'));
const Onboarding = lazy(
  () => import('./onboarding/container/onboarding.container'),
);
const HeaderButtonStylesPage = lazy(
  () => import('./home/routes/dev/header-button-styles.page'),
);

function AppInternalRoutes(): JSX.Element {
  return (
    <Layout>
      <Suspense fallback={<Loader showLoader />}>
        <Switch>
          <Route
            exact
            path="/u/dev/header-button-styles"
            component={HeaderButtonStylesPage}
          />
          <Route component={Home} />
        </Switch>
      </Suspense>
    </Layout>
  );
}

function PurchaseSignIn(): JSX.Element {
  const location = useLocation();
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!signInForPurchase(location.pathname, location.search)) {
      setUnavailable(true);
    }
  }, [location.pathname, location.search]);

  return unavailable ? <Redirect to="/" /> : <Loader showLoader />;
}

function OnboardingLoginRedirect(): JSX.Element {
  stashOnboardingLoginAlert({
    source: 'app.routes auth guard for /onboarding',
    reason: 'No auth token — user is not logged in',
  });
  return <Redirect to="/" />;
}

export default function AppRoutes(): JSX.Element {
  const commonState = useSelector((state: RootState) => state.common);
  const { showLoader } = commonState;
  const { auth, isLoggedIn } = useUserData();
  const location = useLocation();
  const isAuthenticated =
    Boolean(auth?.token) ||
    Boolean(new URLSearchParams(location.search).get('token')) ||
    isDevAuthBypass;
  return (
    <>
      <Suspense fallback={<Loader showLoader />}>
        <Switch>
          <Route
            exact
            path="/terms-and-conditions"
            component={TermsAndConditions}
          />
          <Route exact path="/patentsFoot" component={patentsFoot} />
          <Route
            exact
            path="/terms-and-condition"
            render={() => <Redirect to="/terms-and-conditions" />}
          />
          <Route exact path="/about-us" component={AboutUs} />
          <Route exact path="/live-leaderboard" component={Leaderboard} />
          <Route exact path="/investors" component={InvestorContainer} />
          <Route exact path="/privacy-policy" component={PrivacyPolicy} />
          <Route exact path="/whitepaper" component={Whitepaper} />
          <Route exact path="/cookie-policy" component={CookiePolicy} />
          <Route exact path="/trust-policy" component={TrustPolicy} />
          <Route path="/p-calculator-2" component={PotentialCalculator2} />
          <Route path="/share-wizard" component={ShareWizard} />
          <Route path="/quick-tour" component={QuickTour} />
          <Route path="/quick-tour-home" component={QuickTourHome} />
          <Route path="/verify" component={VerifyEmail} />
          <Route
            exact
            path="/unsubscribe"
            component={UnsubscribePromotionalEmails}
          />
          <Route
            path="/redirect"
            render={() => {
              const hasCode = Boolean(
                new URLSearchParams(location.hash.replace(/^#/, '')).get(
                  'code',
                ),
              );
              return isAuthenticated || hasCode ? (
                <Loader showLoader />
              ) : (
                <Redirect to="/" />
              );
            }}
          />
          <Route path="/quickstart" component={QuickStart} />
          <Route
            path="/onboarding"
            render={() =>
              onboardingRouteIsOpen({
                authenticated: isAuthenticated,
                search: location.search,
                hash: location.hash,
              }) ? (
                <Onboarding />
              ) : (
                <OnboardingLoginRedirect />
              )
            }
          />
          <Route path="/share-wizard" component={ShareWizard} />
          <Route
            path="/u"
            render={() => {
              const go = quickLinkGo(location.pathname);
              if (go) {
                return (
                  <QuickLinkGoPage channelName={go.channelName} linkId={go.linkId} />
                );
              }
              const hasAuthCode = Boolean(
                new URLSearchParams(location.hash.replace(/^#/, '')).get(
                  'code',
                ),
              );
              const people = profilePeoplePath(location.pathname);
              if (people && !isAuthenticated && !hasAuthCode) {
                return <RelationshipListSignIn channelName={people.channelName} />;
              }
              const profileChannel = premiumProfileChannel(location.pathname);
              if (profileChannel && !isAuthenticated && !hasAuthCode) {
                return <PublicProfilePage channelName={profileChannel} />;
              }
              if (isAuthenticated || hasAuthCode) return <AppInternalRoutes />;
              if (isPurchaseDeepLink(location.pathname, location.search)) {
                return <PurchaseSignIn />;
              }
              return <Redirect to="/" />;
            }}
          />
          <Route
            exact={!isLoggedIn}
            path="/:referrerChannelId?"
            component={NewHome}
          />
          <Route render={() => <Redirect to="/" />} />
        </Switch>
      </Suspense>
      <CustomSnackbar />
      {!auth && <CookiePopUp />}
      <Loader showLoader={showLoader} />
    </>
  );
}
